import React, { useEffect, useState } from "react";
import { FormatHelper } from "../../api/FormatHelper";
import { getCurrentSession, getSessions, saveSession, setCurrentSession } from "../../api/SessionApi";
import ContainerCard from "../../common/CardContainer";
import ErrorDisplay from "../../common/ErrorDisplay";
import PageHeader from "../../common/PageHeader";
import { Session, SessionStatuses } from "../../data/Session";
import SessionEdit from "./SessionEdit";
import SessionList, { sessionStatusChipClass } from "./SessionList";

const SessionAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sessions, setSessions] = useState([] as Session[]);
  const [session, setSession] = useState(null as Session | null);
  const [currentSession, setCurrentSessionState] = useState(null as Session | null);
  const [settingCurrentId, setSettingCurrentId] = useState("");

  useEffect(() => {
    getSessions()
      .then((list) => {
        setSessions(list);
      })
      .catch((error) => {
        console.log("Error " + JSON.stringify(error));
        //setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
    refreshCurrentSession();
  }, []);

  function refreshCurrentSession() {
    getCurrentSession()
      .then((current) => setCurrentSessionState(current))
      .catch((error: Error) => setError(`Could not load the current session: ${error.message}`));
  }

  async function onSetCurrent(target: Session) {
    const from = currentSession ? `"${currentSession.name}" (${currentSession.subId})` : "no session";
    if (!window.confirm(`Make "${target.name}" (${target.subId}) the current session?\n\nThis replaces ${from} for all participants.`)) {
      return;
    }
    setError("");
    setSettingCurrentId(target.subId);
    try {
      const updated = await setCurrentSession(target.subId);
      setCurrentSessionState(updated);
    } catch (e: any) {
      setError(`Could not set the current session: ${(e as Error).message}`);
    } finally {
      setSettingCurrentId("");
    }
  }

  function onEdit(session: Session) {
    setSession(session);
  }

  function onNew() {
    setSession({
      name: "New Session",
      dateTimeStart: FormatHelper.getDateNow(),
      dateTimeEnd: FormatHelper.getDateNow(),
      id: "",
      subId: "",
      type: "new",
      status: SessionStatuses.Setup,
      enableParticipantView: false,
      enableParticipantButtons: true,
      askIsInPerson: false,
      currentItemId: "",
    });
  }

  async function onSave(name: string, status: string, enableParticipantView: boolean, enableParticipantButtons: boolean, askIsInPerson: boolean, dateStart: string, dateEnd: string): Promise<string> {
    if (!session) {
      return "No session to save";
    }
    console.log(`Saving session id: ${session.id} name: ${name} status: ${status} dates: ${dateStart} to: ${dateEnd} askIsInPerson: ${askIsInPerson}`);
    var sessionToSave = {
      ...session,
      name: name,
      status: status,
      enableParticipantView: enableParticipantView,
      enableParticipantButtons: enableParticipantButtons,
      askIsInPerson: askIsInPerson,
      dateTimeStart: dateStart,
      dateTimeEnd: dateEnd,
    } as Session;
    try {
      var returnItem = await saveSession(sessionToSave);
      console.log("Returned data: " + returnItem);
      var newList = sessions.map((item) => {
        return item.subId === returnItem.subId ? returnItem : item;
      });
      if (session.id === "") {
        newList.push(returnItem);
      }
      setSession(null);
      setSessions(newList);
      // saving a session can change which session is current (e.g. saving as In Session)
      refreshCurrentSession();
      return "";
    } catch (e: any) {
      let error = e as Error;
      if (error) {
        return error.message;
      }
      return "An unknown error occured";
    }
  }

  function onCancel(): void {
    setSession(null);
  }

  return (
    <>
      <PageHeader title="Sessions" subtitle="Manage synod meeting sessions" />
      <CurrentSessionPanel session={currentSession} />
      <ContainerCard>
        <ErrorDisplay error={error} />
        <SessionList list={sessions} currentSessionId={currentSession?.subId ?? ""} settingCurrentId={settingCurrentId} onEdit={onEdit} onSetCurrent={onSetCurrent} />
        <div className="mt-3 mb-2">
          <button onClick={onNew} className="btn btn-secondary">
            New Session
          </button>
        </div>
      </ContainerCard>
      {session ? <SessionEdit session={session} onSave={onSave} onCancel={onCancel} /> : <></>}
    </>
  );
};

function CurrentSessionPanel({ session }: { session: Session | null }) {
  if (!session) {
    return (
      <div className="alert alert-warning" role="status">
        <strong>No current session.</strong> Participants won't see a meeting until one is set. Use <em>Set as current</em> below.
      </div>
    );
  }
  return (
    <div className="sl-current-session" role="status">
      <span className="sl-current-session-label">Current session</span>
      <span className="sl-num">{session.subId}</span>
      <span className="sl-current-session-name">{session.name}</span>
      <span className={`sl-chip ${sessionStatusChipClass(session.status)}`}>{session.status}</span>
    </div>
  );
}

export default SessionAdmin;
